import { ref, computed } from "vue";
import { defineStore, storeToRefs } from "pinia";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { readErrorMessages, type SaveErrorPayload } from "@utils/composables/useDeltaTracking";
import { captureEvent } from "@/lib/analytics";
import { useSurveyStore } from "@stores/survey/survey";
import { useSectionsStore } from "@stores/survey/sections";
import { useSurveyCardsStore } from "@stores/survey/surveyCards";
import { useToastsStore } from "@stores/toastsStore";
import type { Snapshot } from "@/types";

export const useSnapshotsStore = defineStore("snapshots", () => {
  const surveyStore = useSurveyStore();
  const { token: surveyToken } = storeToRefs(surveyStore);

  const snapshots = ref<Snapshot[]>([]);
  const loading = ref(false);
  const restoringId = ref<number | null>(null);

  const basePath = computed(() => `surveys/${surveyToken.value}/snapshots`);

  const fetch = async () => {
    if (!surveyToken.value) return;
    loading.value = true;
    try {
      const { response, data } = await useMyFetch(basePath.value).get().json<Snapshot[]>();
      snapshots.value = response.value?.ok ? (data.value ?? []) : [];
    } catch (err) {
      handleError(err);
    } finally {
      loading.value = false;
    }
  };

  const reloadEditor = async () => {
    await surveyStore.fetch(surveyToken.value);
    await useSectionsStore().fetch();
    useSurveyCardsStore().selectFirstCardAsCurrent();
  };

  const restore = async (snapshot: Snapshot) => {
    restoringId.value = snapshot.id;
    try {
      const { response, data, error } = await useMyFetch(`${basePath.value}/${snapshot.id}/restore`).post().json<Snapshot[]>();
      if (!response.value?.ok) {
        useToastsStore().error("Restore failed", readErrorMessages(data.value as SaveErrorPayload | null, error.value).join(", "));
        return;
      }
      snapshots.value = data.value ?? [];
      await reloadEditor();
      captureEvent("survey_version_restored", { snapshot_reason: snapshot.reason });
      useToastsStore().success("Version restored", "The questionnaire now matches the selected version.");
    } catch (err) {
      handleError(err);
    } finally {
      restoringId.value = null;
    }
  };

  return { snapshots, loading, restoringId, fetch, restore };
});
